import { cn } from "@eisox/design-system";

import { EMPTY_VALUE } from "~/constants/display.constant";
import { m } from "~/paraglide/messages";

import { CHART_SERIES } from "../consumption.constant";
import { formatConsumption, formatPercent } from "../helpers/energy.helper";
import { difference } from "../helpers/series.helper";
import { SeriesKey, type ConsumptionRow } from "../types/consumption.type";

interface ConsumptionTooltipProps {
  row: ConsumptionRow;
  formatStart: (start: string) => string;
  unit?: string;
  degreeDays: boolean;
  formatComparedStart?: (start: string) => string;
  objectiveLabel?: string;
}

const ConsumptionTooltip: React.FC<ConsumptionTooltipProps> = ({
  row,
  formatStart,
  unit,
  degreeDays,
  formatComparedStart,
  objectiveLabel,
}) => {
  const format = (value: number | undefined) => (value === undefined ? EMPTY_VALUE : formatConsumption(value, unit));
  const columns = <T,>(raw: T, dju: T): T[] => (degreeDays ? [raw, dju] : [raw]);
  const lines = [
    {
      label: formatStart(row.start),
      keys: columns(SeriesKey.CONSUMPTION, SeriesKey.CONSUMPTION_DJU),
      index: format(row.index),
    },
    ...(formatComparedStart
      ? [
          {
            label: formatComparedStart(row.start),
            keys: columns(SeriesKey.COMPARISON, SeriesKey.COMPARISON_DJU),
            index: row.comparisonIndex !== undefined && format(row.comparisonIndex),
          },
        ]
      : []),
    ...(objectiveLabel ? [{ label: objectiveLabel, keys: columns(SeriesKey.OBJECTIVE, SeriesKey.OBJECTIVE_DJU) }] : []),
  ];
  const differences = columns(
    difference([row], SeriesKey.CONSUMPTION, SeriesKey.COMPARISON),
    difference([row], SeriesKey.CONSUMPTION_DJU, SeriesKey.COMPARISON_DJU),
  );

  return (
    <div className="gap-y-2.5 flex w-max flex-col">
      <div
        className={cn(
          "gap-x-2.5 md:gap-x-5 gap-y-1.25 md:text-base grid items-center text-sm text-primary-white",
          degreeDays
            ? "md:grid-cols-[repeat(4,auto)] grid-cols-[repeat(3,auto)]"
            : "md:grid-cols-[repeat(3,auto)] grid-cols-[repeat(2,auto)]",
        )}
      >
        <span />
        {columns(m.consumption(), m.consumption_dju()).map(header => (
          <span key={header} className="text-right text-primary-dark-gray">
            {header}
          </span>
        ))}
        <span className="max-md:hidden text-right text-primary-dark-gray">{m.index()}</span>
        {lines.map(({ label, keys, index }) => [
          <span key={`${label}-label`}>{label}</span>,
          ...keys.map(key => {
            const { color, kind } = CHART_SERIES[key];
            return (
              <span key={key} className="gap-1.25 font-medium flex items-center justify-end tabular-nums">
                <span
                  className={cn("shrink-0", kind === "level" ? "w-3 border-t-2 border-dashed" : "size-2 rounded-full")}
                  style={kind === "level" ? { borderColor: color } : { backgroundColor: color }}
                />
                {format(row[key])}
              </span>
            );
          }),
          <span key={`${label}-index`} className="max-md:hidden text-right tabular-nums">
            {index}
          </span>,
        ])}
        {formatComparedStart && !row.inProgress && (
          <>
            <span>{m.consumption_difference()}</span>
            {differences.map((gap, column) => (
              <span
                key={column}
                className={cn(
                  "font-medium text-right tabular-nums",
                  gap && (gap.ratio > 0 ? "text-primary-red" : "text-primary-green"),
                )}
              >
                {gap ? formatPercent(gap.ratio, { signed: true }) : EMPTY_VALUE}
              </span>
            ))}
            <span className="max-md:hidden" />
          </>
        )}
      </div>
      {row.inProgress && <p className="text-sm text-primary-dark-gray">{m.consumption_slot_in_progress()}</p>}
    </div>
  );
};

ConsumptionTooltip.displayName = "ConsumptionTooltip";

export { ConsumptionTooltip };
